import { partnersImageRecommendedSize } from 'hb-builder/components/BuilderComponents/constants/imageSizes';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import {
  useBuilderContext,
  useComponentContext,
  useBuilderConfigContext,
  usePanelContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import type {
  PartnersItem,
  PartnersItemAttributes,
} from 'hb-react/backoffice/types/builder/itemAttributes/partnersItemAttributes';
import { Col } from 'hb-react/shared/components/DesignSystem/Grid';

import PartnerUI from './components/PartnerUI';
import { PARTNERS_LOGOS } from './constants';

const Partner: BuilderComponent<PartnersItemAttributes> = () => <div />; // never used

const RenderBuilder = () => {
  const { getParentItem, updateAttribute } = useBuilderContext();
  const { canBeDoubleClicked, item } = useComponentContext();
  const {
    hivebrite: { subdomainNetworkUrl },
  } = useBuilderConfigContext();
  const { displayPanel } = usePanelContext();

  const { partnerImageAlt, index, attributes } = item as PartnersItem;
  const {
    _imageData,
    columnWidth,
    logoBackground,
    partnerImage,
    isLoadingSrc,
    openInNewTab,
  } = attributes || {};

  const partnersContainer = getParentItem(item.id);
  const column = getParentItem(partnersContainer?.id);
  const partnersItem = getParentItem(column?.id);

  const parent = item.template ? partnersItem : partnersContainer;

  const toggleDrawer = () => {
    const isOpened = parent.attributes.__isSelected === item.id;

    if (isOpened) {
      return;
    }

    updateAttribute({
      id: parent.id,
      attribute: '__isSelected',
      value: item.id,
    });
  };

  return (
    <Col
      // @ts-expect-error onClick is not defined on ColProps
      onClick={toggleDrawer}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (canBeDoubleClicked) {
          displayPanel();
        }
      }}
      width={columnWidth}
      data-testid="partners-slider-wrapper"
    >
      <PartnerUI
        index={index ? index % PARTNERS_LOGOS.length : 0}
        logoBackground={logoBackground}
        isLoadingSrc={isLoadingSrc}
        openInNewTab={openInNewTab}
        padding={{ _: 3, m: 4 }}
        partnerImage={_imageData || partnerImage}
        partnerImageAlt={partnerImageAlt}
        subdomainNetworkUrl={subdomainNetworkUrl}
      />
      <ImageSetter
        attributeName="partnerImage"
        item={item}
        imageSize={partnersImageRecommendedSize}
      />
    </Col>
  );
};

const Render = ({ item }: RenderProps<PartnersItem>) => {
  const { subdomainNetworkUrl } = useRendererContext();
  const { columnWidth, logoBackground, openInNewTab, partnerImage } =
    item?.attributes || {};
  const { partnerImageAlt, partnerImageUrl } = item || {};

  return (
    <Col width={columnWidth}>
      <PartnerUI
        index={(item?.index || 0) % PARTNERS_LOGOS.length}
        logoBackground={logoBackground}
        openInNewTab={openInNewTab}
        padding={{ _: 3, m: 4 }}
        partnerImage={partnerImage}
        partnerImageAlt={partnerImageAlt}
        partnerImageUrl={partnerImageUrl}
        subdomainNetworkUrl={subdomainNetworkUrl}
      />
    </Col>
  );
};

Partner.componentName = 'Partner';
Partner.componentType = 'single';

Partner.canBeDoubleClicked = true;
Partner.canBeDragged = false;
Partner.canBeListed = false;
Partner.canBeSelected = false;

Partner.RenderBuilder = RenderBuilder;
Partner.Render = Render;

Partner.item = ({
  columnWidth,
  index,
  logoBackground,
  openInNewTab = true,
  partnersCount,
  partnerImage = '',
  partnerImageAlt = '',
  partnerImageUrl = '',
  template,
}) => ({
  type: Partner.componentName,
  attributes: {
    columnWidth,
    logoBackground,
    openInNewTab,
    partnerImage,
  },
  index,
  partnersCount,
  partnerImageAlt,
  partnerImageUrl,
  template,
});

export default Partner;
